/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ProfileContent from "./Settings/ProfileContent";
import AccountSettingsContent from "./Settings/AccountSettingsContent";
import DocumentsContent from "./Settings/DocumentsContent";
import SocialMediaContent from "./Settings/SocialMediaContent";
import NotificationsContent from "./Settings/NotificationsContent";
import AppearanceContent from "./Settings/AppearanceContent";
import { displayMPSProfile } from "../../firebaseFunctions/displayMPSProfile/displayMPSProfile";
import { useAuth } from "../../context/AuthContext";
import ResponsiveTabs from "../ui/ResponsiveTabs";
import ProfileCompletionBanner from "../ui/ProfileCompletionBanner";

const tabs = [
  { value: "Profile", label: "Profile" },
  { value: "Account Settings", label: "Account" },
  { value: "Documents", label: "Documents" },
  { value: "Social Media", label: "Social" },
  { value: "Notifications", label: "Notifications" },
  { value: "Appearance", label: "Appearance" },
];

const getMPSCompletionFields = (profileData) => [
  { label: "Business Name", filled: !!profileData?.businessName?.trim() },
  { label: "Business Type", filled: !!profileData?.typeOfBusiness?.trim() },
  { label: "Phone Number", filled: !!profileData?.phoneNumber?.trim() },
  { label: "Email Address", filled: !!profileData?.email?.trim() },
  { label: "Business Description", filled: !!profileData?.description?.trim() },
  { label: "Company Logo", filled: !!profileData?.companyLogo },
];

const SettingsContent = () => {
  const location = useLocation();
  const urlTab = new URLSearchParams(location.search).get("tab");
  const initialTab = urlTab === "profile" ? "Profile"
    : urlTab === "documents" ? "Documents"
    : urlTab === "notifications" ? "Notifications"
    : "Profile";

  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [profileData, setProfileData] = useState(null);
  const [userSignInMethod, setUserSignInMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const renderContent = () => {
    switch (selectedTab) {
      case "Profile":
        return (
          <>
            <ProfileCompletionBanner
              fields={getMPSCompletionFields(profileData)}
            />
            <ProfileContent
              user={user}
              profileData={profileData}
              userSignInMethod={userSignInMethod}
              reload={fetchProfileData}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
            />
          </>
        );
      case "Account Settings":
        return <AccountSettingsContent userSignInMethod={userSignInMethod} />;
      case "Documents":
        return <DocumentsContent
          profileData={profileData}
          profileDocuments={profileData?.documents}
          user={user}
          reload={fetchProfileData}
        />;
      case "Social Media":
        return <SocialMediaContent />;
      case "Notifications":
        return <NotificationsContent />;
      case "Appearance":
        return <AppearanceContent />;
      default:
        return <ProfileContent />
    }
  }

  const fetchProfileData = async () => {
    if (user) {
      setIsLoading(true);
      try {
        const data = await displayMPSProfile(user.uid);
        setProfileData(data);
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [user]);

  const checkUserSignInMethod = (user) => {
    if (user && user.providerData) {
      const providerData = user.providerData;
      let signInMethod = null;
      providerData.forEach((provider) => {
        if (provider.providerId === "google.com") {
          signInMethod = "Google";
        } else if (provider.providerId === "password") {
          signInMethod = "Email";
        }
      });
      return signInMethod;
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (user) {
      const response = checkUserSignInMethod(user);
      setUserSignInMethod(response);
    }
  }, [user]);

  return (
    <div className="w-full h-full relative shadow-md rounded-lg bg-[#fefefe] flex flex-col items-start justify-start p-4 sm:p-8 box-border text-left text-4xl text-[#101828] font-inter">
      <div className="self-stretch overflow-y-auto flex flex-col items-start justify-start p-4 sm:p-8 gap-4 sm:gap-8">
        <div className="self-stretch bg-white flex flex-col items-start justify-start gap-4">
          <div className="self-stretch relative tracking-[-0.02em] leading-[44px] font-medium flex items-center h-[37px] shrink-0">
            Settings
          </div>
          <div className="w-full max-w-[763px] relative text-base leading-6 flex items-center">
            Manage your account settings and preferences
          </div>
        </div>
        <div className="self-stretch bg-white flex flex-col items-start justify-start gap-4">
          <ResponsiveTabs tabs={tabs} activeTab={selectedTab} onTabChange={setSelectedTab} />
        </div>
        <div className="self-stretch bg-white flex flex-col items-start justify-start gap-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default SettingsContent;
